// Güncellenmiş ServiceDetails.jsx
// Yeni vehicleInventoryWithPrice yapısına göre (daily + hourly[] + gift) okuma yapacak şekilde güncellendi.
// Eski "prices" şeması da fallback olarak destekleniyor.

import React, { useContext, useEffect, useMemo, useState } from "react";
import { TbCreditCard, TbMinus, TbPlus } from "react-icons/tb";
import Button from "../Button/Button";
import { useNavigate } from "react-router-dom";
import { MallContext } from "../../MallContext";
import { useLanguage } from "../../LanguageProvider";

function ServiceDetails({
  handleTimeOptionClick,
  setIsRentalModalOpen,
  selectedService,
  countOptions,
  handleCountOptionClick,
  decrementCount,
  incrementCount,
  countItems,
  type = "new",
  requestID,
  willDeliverDateTime
}) {
  const { getTranslation } = useLanguage();
  const { mallInfo } = useContext(MallContext);
  const navigate = useNavigate();
  const canUsePhysicalPos = String(mallInfo?.hasPhysicalPOS) === "1";
  const canUseCash = String(mallInfo?.isCash) === "1";

  // === key eşlemesi ===
  const vehicleKey = useMemo(() => {
    if (selectedService === "cocuk" || selectedService === "kid-car") return "kid-car";
    if (selectedService === "gift") return "gift";
    return "pet-car";
  }, [selectedService]);

  const DURATION_ORDER = ["30 Dk","1", "2", "4", "6", "12", "1 Gün"];

  // === var olan süreler (yeni şema: hourly[] + daily / eski şema: prices objesi) ===
  const availableDurations = useMemo(() => {
    const inv = mallInfo?.vehicleInventoryWithPrice;
    const node = inv?.[vehicleKey];
    if (!node || vehicleKey === "gift") return [];

    const durationsSet = new Set();

    // Yeni şema: hourly[]
    if (Array.isArray(node.hourly)) {
      node.hourly.forEach((h) => {
        if (!h) return;
        const key = String(h.hour ?? h.duration ?? "");
        if (key) durationsSet.add(key);
      });
    }

    // Yeni şema: daily
    if (node.daily) {
      durationsSet.add("1 Gün");
    }

    // Eski şema fallback: prices objesi
    if (
      durationsSet.size === 0 &&
      node.prices &&
      typeof node.prices === "object"
    ) {
      Object.keys(node.prices)
        .filter((k) => k !== "-")
        .forEach((k) => durationsSet.add(k));
    }

    const arr = Array.from(durationsSet);
    if (!arr.length) return [];

    return arr.sort((a, b) => {
      const ia = DURATION_ORDER.indexOf(a);
      const ib = DURATION_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }, [mallInfo, vehicleKey]);

  // === seçili süre ===
  const [selectedDurationKey, setSelectedDurationKey] = useState("1");

  useEffect(() => {
    if (vehicleKey === "gift") return;
    if (!availableDurations.length) return;
    setSelectedDurationKey((prev) =>
      availableDurations.includes(prev) ? prev : availableDurations[0]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleKey, availableDurations.join("|")]);

  useEffect(() => {
    if (vehicleKey === "gift") return;
    if (!availableDurations.length) return;
    handleTimeOptionClick?.(selectedDurationKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDurationKey]);

  // === para birimi ===
  const toDisplayCurrency = (c) => (c === "TRY" ? "TL" : (c || "TL"));

  // === fiyat hesaplama ===
  const [unitPrice, setUnitPrice] = useState("");
  const [unitCurrency, setUnitCurrency] = useState("TL");

  useEffect(() => {
    const inv = mallInfo?.vehicleInventoryWithPrice;
    const node = inv?.[vehicleKey];
    if (!node) return;

    // --- Gift: yeni şema { price, currency, status } / eski şema fallback ---
    if (vehicleKey === "gift") {
      let price;
      let currency;

      // yeni şema
      if (typeof node.price !== "undefined") {
        price = node.price;
        currency = node.currency;
      }
      // eski şema: prices["-"]
      else if (node.prices?.["-"]) {
        price = node.prices["-"].price;
        currency = node.prices["-"].currency;
      }

      if (typeof price !== "undefined") {
        setUnitPrice(price ?? "");
        setUnitCurrency(toDisplayCurrency(currency));
      } else {
        setUnitPrice("");
        setUnitCurrency("TL");
      }

      return;
    }

    // --- pet-car / kid-car: yeni şema (daily + hourly[]) ---
    let price;
    let currency;

    // 1 Gün → daily
    if (selectedDurationKey === "1 Gün" && node.daily) {
      price = node.daily.price;
      currency = node.daily.currency;
    } else if (Array.isArray(node.hourly)) {
      const match = node.hourly.find(
        (h) =>
          String(h.hour ?? h.duration ?? "") === String(selectedDurationKey)
      );
      if (match) {
        price = match.price;
        currency = match.currency;
      }
    }

    // Eski şema fallback: prices[duration]
    if (
      typeof price === "undefined" &&
      node.prices &&
      typeof node.prices === "object"
    ) {
      const p = node.prices?.[selectedDurationKey];
      if (p) {
        price = p.price;
        currency = p.currency;
      }
    }

    if (typeof price !== "undefined") {
      setUnitPrice(price ?? "");
      setUnitCurrency(toDisplayCurrency(currency));
    } else {
      setUnitPrice("");
      setUnitCurrency("TL");
    }
  }, [mallInfo, vehicleKey, selectedDurationKey]);

  // === aktiflik ===
  const isVehicleTypeActive =
    mallInfo?.vehicleInventoryWithPrice?.[vehicleKey]?.status ?? false;

  // === ödeme ===
  const [step, setStep] = useState("details");
  const [paymentType, setPaymentType] = useState("withCreditCard");
  const isCashPayment =
    paymentType === "withCash" || paymentType === "withPhysicalPosCash";
  const isPhysicalPosPayment = paymentType === "withPhysicalPos";
  useEffect(() => {
    if (isPhysicalPosPayment && !canUsePhysicalPos) {
      setPaymentType("withCreditCard");
      return;
    }

    if (isCashPayment && !canUseCash) {
      setPaymentType("withCreditCard");
    }
  }, [canUseCash, canUsePhysicalPos, isCashPayment, isPhysicalPosPayment]);

  const proceed = () => {
    setIsRentalModalOpen(false);
    navigate("/rent-confirmation", {
      state: {
        selectedService,
        dailyPrice: unitPrice,
        priceCurrency: unitCurrency,
        packageCount: countItems,
        paymentType,
        selectedDurationKey,
        type,
        requestID,
        originalWillDeliverDateTime: willDeliverDateTime
      }
    });
  };

  // === buton navigasyonu ===
  const onMinusTime = () => {
    if (vehicleKey === "gift") {
      decrementCount();
      return;
    }
    if (!availableDurations.length) return;
    const curIdx = Math.max(0, availableDurations.indexOf(selectedDurationKey));
    const nextIdx = curIdx > 0 ? curIdx - 1 : availableDurations.length - 1;
    setSelectedDurationKey(availableDurations[nextIdx]);
  };

  const onPlusTime = () => {
    if (vehicleKey === "gift") {
      incrementCount();
      return;
    }
    if (!availableDurations.length) return;
    const curIdx = Math.max(0, availableDurations.indexOf(selectedDurationKey));
    const nextIdx = (curIdx + 1) % availableDurations.length;
    setSelectedDurationKey(availableDurations[nextIdx]);
  };

  // === çeviri etiketleri ===
  const getDurationLabel = (key) => {
    if (key === "1 Gün") return getTranslation("daily");          
    if (key === "30 DK") return `30 ${getTranslation("min")}`; 
  
    return `${key} ${getTranslation("hour")}`;
  };

  // === render ===
  const renderDetailsBody = () => (
    <>
      {vehicleKey === "gift" ? (
        <>
          <div className="time-control" style={{ marginTop: "15px" }}>
            <button onClick={onMinusTime} className="time-btn minus-btn">
              <TbMinus />
            </button>
            <span className="rental-modal-time-display">
              {countItems} {getTranslation("piece")}
            </span>
            <button onClick={onPlusTime} className="time-btn plus-btn">
              <TbPlus />
            </button>
          </div>

          <div className="time-options">
            {countOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => handleCountOptionClick(opt)}
                className="time-option-btn"
              >
                {opt}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="time-control" style={{ marginTop: "26px" }}>
            <button onClick={onMinusTime} className="time-btn minus-btn">
              <TbMinus />
            </button>
            <span className="rental-modal-time-display">
              {getDurationLabel(selectedDurationKey)}
            </span>
            <button onClick={onPlusTime} className="time-btn plus-btn">
              <TbPlus />
            </button>
          </div>

          <div className="time-options">
            {availableDurations.length === 0 ? (
              <span style={{ fontSize: 13, color: "rgba(120,120,120,1)" }}>
                {getTranslation("noAvailableDurations") ||
                  "Bu hizmet için uygun süre bulunmuyor."}
              </span>
            ) : (
              availableDurations.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setSelectedDurationKey(opt)}
                  className={`time-option-btn ${opt === selectedDurationKey ? "active" : ""
                    }`}
                >
                  {getDurationLabel(opt)}
                </button>
              ))
            )}
          </div>
        </>
      )}

      {unitPrice !== "" && (
        <div className="fee-info">
          <span
            style={{
              width: "80%",
              borderBottom: "1px solid rgba(230, 230, 230, 1)",
              paddingBottom: "16px",
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
              color: "rgba(72, 72, 72, 1)",
              fontSize: "16px",
              fontWeight: "600",
              marginTop: "26px",
              marginBottom: "23px"
            }}
          >
            {getTranslation("feeInformation")}
          </span>
          <div className="fee-amount">
            {String(unitPrice)} {toDisplayCurrency(unitCurrency)}
          </div>
        </div>
      )}

      {/* TODO: Kuponlar için alttaki yeri aktif edip devam edilecek */}
      {/* <div className="detail-footer" style={{ marginTop: 18 }}>
        <Button
          disabled={unitPrice === "" || !isVehicleTypeActive}
          icon={<TbCreditCard size={22} />}
          onClick={proceed}
          label={getTranslation("proceedToPayment")}
          style={{ width: "280px", backgroundColor: "#de4b4b" }}
        />
      </div> */}

      <div className="detail-footer">
        <Button
          disabled={unitPrice === "" || !isVehicleTypeActive}
          icon={<TbCreditCard size={22} />}
          onClick={() => setStep("paymentType")}
          label={getTranslation("choosePaymentType")}
          style={{ width: "280px" }}
        />
      </div>
    </>
  );

  const renderPaymentTypeBody = () => (
    <>
      <span
        style={{
          width: "100%",
          borderBottom: "1px solid rgba(230, 230, 230, 1)",
          paddingBottom: "16px",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
          color: "rgba(72, 72, 72, 1)",
          fontSize: "16px",
          fontWeight: "600",
          marginTop: "6px",
          marginBottom: "16px"
        }}
      >
        {getTranslation("selectPaymentTypeTitle") || "Ödeme Tipi Seçin"}
      </span>

      <div style={{ width: "100%", padding: "0 18px", boxSizing: "border-box" }}>
        <label
          htmlFor="paymentType"
          style={{
            display: "block",
            fontSize: 13,
            color: "rgba(72,72,72,1)",
            marginBottom: 8
          }}
        >
          {getTranslation("selectType") || "Tip Seçin"}
        </label>

        <div
          style={{
            background: "#fff",
            border: "1px solid rgba(230,230,230,1)",
            borderRadius: 8,
            padding: "10px 12px"
          }}
        >
          <select
            id="paymentType"
            value={paymentType}
            onChange={(e) => setPaymentType(e.target.value)}
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 14,
              color: "#333"
            }}
          >
            <option value="withCreditCard">
              {getTranslation("withCreditCard") || "Kredi Kartı ile"}
            </option>
            <option value="withCoupon">
              {getTranslation("withCoupon") || "Kupon ile"}
            </option>
            {canUsePhysicalPos && (
              <option value="withPhysicalPos">
                {getTranslation("withPhysicalPos") || "Fiziksel POS"}
              </option>
            )}
            {canUseCash && (
              <option value="withCash">
                {getTranslation("withCash") || "Nakit"}
              </option>
            )}
          </select>
        </div>
      </div>

      <div className="detail-footer" style={{ marginTop: 18 }}>
        <Button
          disabled={unitPrice === "" || !isVehicleTypeActive}
          icon={<TbCreditCard size={22} />}
          onClick={proceed}
          label={getTranslation("proceedToPayment")}
          style={{ width: "280px", backgroundColor: "#de4b4b" }}
        />
      </div>
    </>
  );

  return (
    <div className="service-detail-container">
      {step === "details" ? renderDetailsBody() : renderPaymentTypeBody()}
    </div>
  );
}

export default ServiceDetails;
